import { jsx as _jsx } from "react/jsx-runtime";
import { ComponentSync, StorageKey } from '@nu-art/thunderstorm-frontend/index';
import { ModuleFE_Pathway } from '@app/pathway-frontend';
import { asArray } from '@nu-art/ts-common';
import { ModuleFE_VolatileImport } from '../ModuleFE_VolatileImport.js';
import { Component_ResizableReviewPanel } from '../Component_ResizableReviewPanel/Component_ResizableReviewPanel.js';
// KM-local width preference for the pathway review overlay (px). Distinct key from the DP review.
const StorageKey_ReviewWidth = new StorageKey('pathway-review-html__width');
/**
 * Renders the optional pathway HTML into the `pathway-html` slot, docked as an overlay over the graph
 * region (mounted from Manager_Pathway.render_Graph). Shown only while the review is open (toggled via
 * the "Review" section link or this panel's X). Shows the saved Storage URL when the pathway has one;
 * otherwise, on a volatile preview, shows the locally-attached HTML File via an object URL (revoked on
 * unmount). The iframe is fully sandboxed (sandbox="") with no referrer so the download token never
 * leaks via the Referer header.
 */
export class Component_PathwayHtml extends ComponentSync {
    __onPathwayReviewToggled = (reviewKey) => {
        if (reviewKey === `${this.props.tabId}::${this.props.pathwayId}`)
            this.reDeriveState();
    };
    // Re-derive when this pathway's cache entry changes (e.g. HTML attached/replaced/removed in edit
    // mode) so the iframe src reflects the new pathwayHtmlUrl without needing to toggle the overlay.
    __onPathwayUpdated = (...params) => {
        if (asArray(params[1]).every(pathway => pathway._id !== this.props.pathwayId))
            return;
        this.reDeriveState();
    };
    deriveStateFromProps(nextProps, state) {
        state.isOpen = ModuleFE_VolatileImport.isReviewOpen(nextProps.tabId, nextProps.pathwayId);
        const pathway = ModuleFE_Pathway.cache.unique(nextProps.pathwayId);
        state.pathwayLabel = pathway?.label ?? '';
        const savedUrl = pathway?.pathwayHtmlUrl;
        if (savedUrl) {
            this.revoke(state.objectUrl); // drop any local object URL we were holding
            state.src = savedUrl;
            state.objectUrl = undefined;
            return state;
        }
        // Not saved yet: show the local file the user attached to this preview, if any.
        const importId = ModuleFE_VolatileImport.getImportIdForPathway(nextProps.tabId, nextProps.pathwayId);
        const file = importId ? ModuleFE_VolatileImport.getPendingHtmlFile(importId) : undefined;
        if (!file) {
            this.revoke(state.objectUrl);
            state.src = undefined;
            state.objectUrl = undefined;
            return state;
        }
        // Reuse the object URL across re-renders; create one per mount (the slot is keyed by tabId).
        if (state.objectUrl)
            return state;
        const objectUrl = URL.createObjectURL(file);
        state.src = objectUrl;
        state.objectUrl = objectUrl;
        return state;
    }
    componentWillUnmount() {
        this.revoke(this.state.objectUrl);
    }
    // Prototype method (not an instance arrow field): deriveStateFromProps runs inside the base
    // constructor, before instance fields are assigned, so an arrow field here would be undefined.
    revoke(url) {
        if (url)
            URL.revokeObjectURL(url);
    }
    render() {
        if (!this.state.src || !this.state.isOpen)
            return null;
        return _jsx(Component_ResizableReviewPanel, { storageKey: StorageKey_ReviewWidth, title: 'Review', label: this.state.pathwayLabel, src: this.state.src, onClose: () => ModuleFE_VolatileImport.closeReview(this.props.tabId, this.props.pathwayId) });
    }
}
//# sourceMappingURL=Component_PathwayHtml.js.map